import React, { useEffect, useState } from "react";
import { useTranslation } from "shared/contexts/I18nContext";

const CourseDetailsModal = ({ isOpen, onClose, course, studentId, onEnroll, getLabelByCode }) => {
  const { t } = useTranslation();
  const [isRendered, setIsRendered] = useState(isOpen);
  const [isVisible, setIsVisible] = useState(false);

  // Handle animation states
  useEffect(() => {
    if (isOpen) {
      setIsRendered(true);
      // Small delay to ensure the DOM is rendered before animating
      setTimeout(() => setIsVisible(true), 10);
      document.body.style.overflow = "hidden"; // Prevent background scrolling
    } else {
      setIsVisible(false);
      // Wait for animation to finish before removing from DOM
      setTimeout(() => {
        setIsRendered(false);
        document.body.style.overflow = "unset";
      }, 500); 
    }
  }, [isOpen]);

  if (!isRendered || !course) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end overflow-hidden">
      {/* Cinematic Backdrop */}
      <div 
        className={`absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity duration-500 ${
          isVisible ? "opacity-100" : "opacity-0"
        }`}
        onClick={onClose}
      ></div>

      {/* Slide-over Drawer */}
      <div 
        className={`relative w-full max-w-lg h-full bg-white dark:bg-gray-900 shadow-2xl flex flex-col transform transition-transform duration-500 ease-in-out ${
          isVisible ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Cover Graphic */}
        <div className="relative h-64 w-full bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500">
          <button
            onClick={onClose}
            className="absolute top-6 right-6 w-10 h-10 rounded-full bg-black/20 hover:bg-black/40 text-white flex items-center justify-center transition-colors backdrop-blur-md"
          >
            <span className="text-xl">×</span>
          </button>
          
          {/* Avatar overlaying the cover */}
          <div className="absolute -bottom-10 left-8">
            <div className="w-20 h-20 rounded-full border-4 border-white dark:border-gray-900 bg-gray-100 dark:bg-gray-800 flex items-center justify-center shadow-lg overflow-hidden relative">
               <span className="text-2xl font-bold text-gray-400">
                 {course.tutor?.user?.firstName?.charAt(0) || "T"}
               </span>
            </div>
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto pt-14 pb-32 px-8">
          <div>
            <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight leading-tight">
              {course.courseName}
            </h2>
            <p className="mt-2 text-lg font-medium text-gray-500 dark:text-gray-400">
              Instructor: <span className="text-gray-900 dark:text-gray-300">{course.tutor?.user?.firstName} {course.tutor?.user?.lastName}</span>
            </p>
          </div>

          <div className="mt-10">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">About this course</h3>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-lg">
              {course.description || "Discover the fundamentals and advanced concepts in this comprehensive course. Perfect for students looking to excel in their academic journey."}
            </p>
          </div>

          {/* Details Bento Grid */}
          <div className="mt-10 grid grid-cols-2 gap-4">
            {[
              { label: 'Curriculum', value: getLabelByCode('curricula', course.curriculum) },
              { label: 'Class', value: getLabelByCode('academicClasses', course.academicClass) },
              { label: 'Subject', value: getLabelByCode('subjects', course.subject) },
              { label: 'Format', value: "Online / Hybrid" }, // Mock data to fill the grid
            ].map((item, index) => (
              <div key={index} className="bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-5 border border-gray-100 dark:border-gray-700/50">
                <span className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">
                  {item.label}
                </span>
                <span className="block text-base font-semibold text-gray-900 dark:text-white">
                  {item.value || "-"}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Sticky Action Bar */}
        <div className="absolute bottom-0 w-full bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-t border-gray-200 dark:border-gray-800 p-6">
          <div className="flex items-center justify-between max-w-lg mx-auto">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Ready to start?</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">Free Enrollment</p>
            </div>
            <button
              onClick={() => {
                onClose();
                setTimeout(() => onEnroll(studentId, course.id), 300); // Wait for drawer to close slightly
              }}
              className="bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100 hover:scale-105 transition-all duration-300 font-bold text-base px-10 py-4 rounded-xl shadow-[0_4px_14px_0_rgb(0,0,0,0.39)] dark:shadow-[0_4px_14px_0_rgb(255,255,255,0.39)]"
            >
              {t("Enroll Now")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseDetailsModal;
